











































import { Component, Provide, ProvideReactive, Vue } from 'vue-property-decorator';

import { Entities, Fault, Org, ProductType, SimproJobDetails, SitesApi } from '@/api';
import { Constants } from '@/common/Constants';
import { FeedProduct, FeedProductModals } from './models';
import { FeedProductService } from './services/feed-product-service';

import Toast from '@/components/display/toast.vue';
import SecondaryNavigation from '@/components/navbars/secondary-nav.vue';
import ProductActionsBar from '@/components/ProductActionsBar.vue';
import FeedProductTabBar from './components/FeedProductTabBar.vue';
import AddJob from '@/components/add business objects/addJob.vue';
import AddFault from '@/components/add business objects/addFault.vue';
import BillingSetupModal from '@/components/billing-setup-form/billing-setup-modal.vue';
import AddFeedTankModal from './components/AddFeedTankModal.vue';
import ProductTrialModal from '@/components/ProductTrialModal.vue';
import { getProductAllowedUserCount, getProductAllowedUserIds } from '@/services/user-service';
import AddJobModalsContainer from '@/components/add business objects/add-job/AddJobModalsContainer.vue';
import ErrorDiv from '@/components/display/errorDiv.vue';
import { ParsingUtil } from '@/common/ParsingUtil';
import { SimproService } from '@/services/simpro-service';
import { mixins } from 'vue-class-component';
import TitleMixin from '@/mixins/title-mixin';

@Component({
	components: {
		ErrorDiv,
		AddJobModalsContainer,
		SecondaryNavigation,
		ProductActionsBar,
		FeedProductTabBar,
		AddJob,
		AddFault,
		BillingSetupModal,
		AddFeedTankModal,
		ProductTrialModal,
	},
})
export default class FeedProductDetailPage extends mixins(TitleMixin) {
	@ProvideReactive('feedProduct')
	public feedProduct: FeedProduct | null = null;

	@ProvideReactive('feedProductAllowedUserIds')
	public allowedUserIds: number[] | null = null;

	@ProvideReactive('feedCanAddUser')
	public canAddUser: boolean = false;

	@Provide('refreshFeedProduct')
	public refreshFeedProduct: () => Promise<void> = this.refresh;

	@Provide('feedProductService')
	public service: FeedProductService = new FeedProductService();

	@Provide('setSimProJobsExist')
	public setSimProJobsExist: (jobsExist: boolean) => void = this.setJobsExist;

	@ProvideReactive('feedSimproJobs')
	public simproJobs: SimproJobDetails[] = [];

	@ProvideReactive('feedPartnerOrgs')
	public partnerOrgs: Org[] = [];

	public entityTypes: Entities[] = [Entities.FeedTanks];

	public jobsExist: boolean = false;
	public loading: boolean = true;

	private productId: number | null = null;
	private toast: Toast = new Toast();
	private sitesApi: SitesApi = new SitesApi();

	public loadError: string = '';

	public pageTitle() {
		return `Feed | ${this.feedProduct ? this.feedProduct.name : ''}`;
	}

	public async created() {
		this.productId = this.productIdFromRoute();
		await this.onCreated();

		this.$watch(
			() => this.$route.params.productId,
			async (to, from) => {
				this.productId = ParsingUtil.parseId(to) ?? this.productId;
				await this.onCreated();
				await this.refreshFeedProduct();
			},
		);
	}

	public async onCreated(): Promise<void> {
		this.loading = true;
		if (this.productId == null) {
			this.loadError = `Failed to load product for ID '${this.$route.params.productId}'`;
			this.loading = false;
			return;
		}

		this.feedProduct = await this.fetchProduct();
		this.partnerOrgs = await this.fetchPartnerOrgs();
		this.simproJobs = await this.fetchSimproJobs();
		this.loading = false;
	}

	public onFaultCreated(newFault: Fault): void {
		this.feedProduct?.faults.push(newFault);
	}

	public async onJobCreated(): Promise<void> {
		await this.refresh();
	}

	public async createFeedTank(params: { nominalVolume: number; name: string }): Promise<void> {
		if (this.productId === null) {
			return;
		}
		const createTankResponse = await this.service.createFeedTank({
			...params,
			productId: this.productId,
		});
		if (createTankResponse.errorMessage) {
			this.toast.makeToast(createTankResponse.errorMessage, 'Error creating feed tank', 'danger');
			return;
		}
		this.toast.makeToast('Feed tank created', 'Success', 'success');
		await this.refresh();
	}

	public async onTankCreated(): Promise<void> {
		await this.refresh();
		this.$router.push({ name: 'products.feed.feedtanks.overview' });
	}

	private setJobsExist(jobsExist: boolean) {
		this.jobsExist = jobsExist;
	}

	private async refresh(): Promise<void> {
		this.feedProduct = await this.fetchProduct();
	}

	private async fetchProduct(): Promise<FeedProduct | null> {
		if (this.productId === null) {
			return null;
		}
		const productResponse = await this.service.fetchProduct(this.productId);
		this.allowedUserIds = await getProductAllowedUserIds(this.productId);
		const allowedUserCount = await getProductAllowedUserCount(this.productId);
		this.canAddUser =
			allowedUserCount == null ||
			(productResponse?.product?.users != null &&
				productResponse.product?.users?.length < allowedUserCount);
		if (productResponse.errorMessage) {
			this.toast.makeToast(productResponse.errorMessage, 'Error', 'danger');
			return null;
		}
		if (productResponse.product?.type !== ProductType.Feed) {
			return null;
		}
		return productResponse.product ?? null;
	}

	private productIdFromRoute(): number | null {
		return ParsingUtil.parseId(this.$route.params.productId);
	}

	private async fetchPartnerOrgs(): Promise<Org[]> {
		if (!this.feedProduct?.id) {
			return [];
		}
		const partnerOrgsResponse = await this.service.fetchPartnerOrgs(this.feedProduct.id);
		if (partnerOrgsResponse.errorMessage) {
			this.toast.makeToast(partnerOrgsResponse.errorMessage, 'Error', 'danger');
			return [];
		}
		return partnerOrgsResponse.orgs ?? [];
	}

	private async fetchSimproJobs(): Promise<SimproJobDetails[]> {
		if (!this.feedProduct?.siteId) return [];
		const { data } = await this.sitesApi.getSiteById(this.feedProduct.siteId, {
			params: {
				include: 'simprojobs',
			},
		});

		return (<any>data).simprojobs;
	}
}
